import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import axios, { pluckData } from 'src/utilities/http-client'
import * as yup from 'yup'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CancelIcon, CorrectIcon, EditIcon } from 'src/v3/core/icons'
import { useFormik } from 'formik'
import { useState } from 'react'
import copyToClipboard from 'src/utilities/copy-to-clipboard'
import {
  selectSelectedLinkedService,
  errorMessageChanged,
  selectLinkedServiceById,
  fetchLinkedServices,
  successMessageChanged,
} from '../admin.slice'

export default () => {
  let dispatch = useAppDispatch()
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)
  let linkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedService),
  )
  let [updatingOrganizationID, setUpdatingOrganizationID] =
    useState<boolean>(false)
  let [editingOrganizationID, setEditingOrganizationID] =
    useState<boolean>(false)
  let [ssoSelfServiceURL, setSsoSelfServiceURL] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      organizationID: yup.string().nullable(),
    }),
    enableReinitialize: true,
    initialValues: {
      organizationID: linkedService?.organizationID || '',
    },
    onSubmit: (values) => {
      setUpdatingOrganizationID(true)
      axios
        .put('/api/admin/linked-service/organization-id', {
          id: selectedLinkedService,
          organizationID: values.organizationID,
        })
        .then(() => dispatch(fetchLinkedServices()))
        .then(() => setUpdatingOrganizationID(false))
        .then(() => form.resetForm())
        .catch((err) => {
          dispatch(
            errorMessageChanged(
              err?.message || 'Error Updating Organization ID',
            ),
          )
          dispatch(fetchLinkedServices())
          setUpdatingOrganizationID(false)
        })
    },
  })

  let handleCopy = async (url: string) => {
    try {
      await copyToClipboard(url)
      dispatch(successMessageChanged('Copied to clipboard'))
    } catch (err) {
      dispatch(
        errorMessageChanged(
          'Error copying to clipboard. Please try again or manually copy the value.',
        ),
      )
    }
  }

  return (
    <Box sx={{ mt: 1, width: 200 }}>
      <Typography variant="caption" color="text.secondary">
        SSO
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        <Chip
          icon={
            linkedService?.organizationID ? <CorrectIcon /> : <CancelIcon />
          }
          label={
            linkedService?.organizationID
              ? `Active: ${linkedService?.organizationID}`
              : 'Inactive'
          }
          variant={linkedService?.organizationID ? 'filled' : 'outlined'}
          color={linkedService?.organizationID ? 'success' : 'error'}
        />
        <IconButton
          size="small"
          onClick={() => setEditingOrganizationID(!editingOrganizationID)}
        >
          <EditIcon />
        </IconButton>
      </Box>
      {editingOrganizationID && (
        <form onSubmit={form.handleSubmit}>
          <TextField
            sx={{ my: 1 }}
            variant="outlined"
            size="small"
            label="Organization ID"
            name="organizationID"
            type="text"
            fullWidth
            value={form.values.organizationID}
            onChange={form.handleChange}
            error={form.touched.organizationID && !!form.errors.organizationID}
          />
          {!updatingOrganizationID ? (
            <Button type="submit" variant="contained" size="small">
              Update
            </Button>
          ) : (
            <CircularProgress size={30} />
          )}
        </form>
      )}
      {linkedService?.organizationID && (
        <>
          <Button
            className="mt-2"
            variant="contained"
            size="small"
            onClick={() => {
              axios
                .post(
                  `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/sso-ticket`,
                  {
                    organizationID: linkedService?.organizationID,
                    name: linkedService?.name,
                  },
                )
                .then(pluckData)
                .then(setSsoSelfServiceURL)
                .catch((err) =>
                  dispatch(
                    errorMessageChanged(
                      err?.message || 'Error generating sso ticket',
                    ),
                  ),
                )
            }}
          >
            Generate SSO Ticket
          </Button>
          {ssoSelfServiceURL && (
            <code className="inline-flex items-center p-4 pl-6 mt-2 space-x-4 text-sm text-left text-white bg-gray-800 rounded-lg sm:text-base">
              <span className="flex gap-4">
                <span className="flex-1">
                  <span>{ssoSelfServiceURL}</span>
                </span>
              </span>
              <span onClick={() => handleCopy(ssoSelfServiceURL)}>
                <svg
                  className="w-5 h-5 text-gray-500 transition shrink-0 group-hover:text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z"></path>
                  <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z"></path>
                </svg>
              </span>
            </code>
          )}
        </>
      )}
    </Box>
  )
}
